import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { getAuctionSocketUrl } from '../flags';

const AUCTION_EVENTS = [
  'auction.bid_created',
  'auction.price_updated',
  'auction.awarded',
  'auction.closed',
  'auction.expired',
];

export const sessionUserCodFromToken = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }
  try {
    const part = token.split('.')[1];
    if (!part) {
      return null;
    }
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(padded));
    const data = json?.data || json;
    return data?.cod ?? null;
  } catch (e) {
    return null;
  }
};

export const useAuctionSocket = ({
  cod,
  token,
  extraRooms = [],
  enabled = true,
  onEvent,
}) => {
  const [connected, setConnected] = useState(false);
  const [socketDown, setSocketDown] = useState(false);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled || !token || (!cod && extraRooms.length === 0)) {
      return undefined;
    }
    const url = getAuctionSocketUrl();
    if (!url) {
      setSocketDown(true);
      return undefined;
    }

    const socket = io(url, {
      transports: ['websocket', 'polling'],
      reconnection: true,
    });

    const room = cod ? `subasta:${cod}` : null;
    const userCod = sessionUserCodFromToken(token);
    const userRoom = userCod ? `subasta:user:${userCod}` : null;
    const privateRooms = extraRooms.filter((extra) => extra && extra !== room && extra !== userRoom);
    const joinAll = () => {
      if (room) {
        socket.emit('auction:join', { room, token });
      }
      if (userRoom) {
        socket.emit('auction:join', { room: userRoom, token });
      }
      privateRooms.forEach((extra) => {
        socket.emit('auction:join', { room: extra, token });
      });
    };

    socket.on('connect', () => {
      setConnected(true);
      setSocketDown(false);
      joinAll();
    });
    socket.on('disconnect', () => {
      setConnected(false);
      setSocketDown(true);
    });
    socket.on('connect_error', () => {
      setConnected(false);
      setSocketDown(true);
    });
    socket.on('auction.join_denied', () => {
      setConnected(false);
      setSocketDown(true);
    });

    AUCTION_EVENTS.forEach((eventName) => {
      socket.on(eventName, (payload) => {
        if (onEventRef.current) {
          onEventRef.current(eventName, payload || {});
        }
      });
    });

    return () => {
      if (room) {
        socket.emit('auction:leave', { room });
      }
      if (userRoom) {
        socket.emit('auction:leave', { room: userRoom });
      }
      privateRooms.forEach((extra) => {
        socket.emit('auction:leave', { room: extra });
      });
      AUCTION_EVENTS.forEach((eventName) => socket.off(eventName));
      socket.off('auction.join_denied');
      socket.disconnect();
    };
  }, [cod, token, enabled, extraRooms.join('|')]);

  return { connected, socketDown };
};
